import { useMemo } from 'react'

import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { IoMdRemoveCircleOutline } from 'react-icons/io'
import { useSelector } from 'react-redux'

import {
  Box,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'

import { Modal } from '@/components/overlays'
import { Button } from '@/components/ui'
import { useUpdateDeviceMutation } from '@/graphql/generated/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { mixpanel } from '@/utils/analytics'

import { TourPlanI } from '../types/types'
import { RemoveCheckpointWarning } from './RemoveCheckpointWarning'

interface RemoveCheckPointModalIProps {
  isOpen: boolean
  onClose: () => void
  checkpoint: any
  floorId: string
  facilityId: string
  tourPlans: TourPlanI[]
}

export const RemoveCheckPointModal = ({
  isOpen,
  onClose,
  checkpoint,
  floorId,
  facilityId,
  tourPlans,
}: RemoveCheckPointModalIProps) => {
  const me = useSelector(selectMe)
  const { shouldEnableDeleteCheckpoint } = usePermissions()
  const { showSuccess, showError } = useToasts()
  const [updateDevice, { loading: isLoading }] = useUpdateDeviceMutation()

  const tourPlansWithCheckpoint = useMemo(
    () =>
      tourPlans?.filter((plan) => plan.checkpointIds.includes(checkpoint?.id)),
    [tourPlans, checkpoint]
  )
  const canRemovedCheckpoint = tourPlansWithCheckpoint?.length > 0

  const handleRemoveCheckPoint = async () => {
    const data = {
      id: checkpoint?.id,
      x: null,
      y: null,
    }
    try {
      await updateDevice({
        variables: {
          input: data,
        },
        refetchQueries: [
          'FloorDevicesCount',
          'FacilityDevicesByFloors',
          'FloorsFacilityCheckpoints',
        ],
      })
      showSuccess('Successfully removed Checkpoint')
      onClose()
      mixpanel.track(
        'Button Press: Map Checkpoint - Remove from map - Remove',
        {
          checkpoint_id: checkpoint.id,
          facility_id: facilityId,
          floor_id: floorId,
          role: me.roleName,
        }
      )
    } catch (e) {
      showError('Error removing Checkpoint')
    }
  }

  const handleOnClose = () => {
    onClose()
    mixpanel.track(
      'Button Press: Map Checkpoint - Remove from floor - Cancel',
      {
        checkpoint_id: checkpoint.id,
        facility_id: facilityId,
        floor_id: floorId,
        role: me.roleName,
      }
    )
  }

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={handleOnClose}
    >
      <ModalOverlay />
      <ModalContent bg='#fff' overflow='hidden'>
        <ModalHeader>
          <Box alignItems='center'>
            <Box ml={3} mt={2}>
              <Box
                color='#353849'
                fontSize='22px'
                fontWeight='extrabold'
                letterSpacing='-0.4px'
                lineHeight='1'
              >
                Remove from floor
              </Box>
            </Box>
          </Box>
        </ModalHeader>
        <ModalCloseButton color='#000' mt={2} opacity={0.4} />
        <>
          <ModalBody px='10' py='6'>
            <Box
              color='#8c8d97'
              fontSize='16px'
              fontWeight='medium'
              letterSpacing='-0.4px'
            >
              Removing this checkpoint will make it no longer placed, this will
              also remove it from all tourplans. To place it back, just drag it
              onto any floor.
            </Box>
            {canRemovedCheckpoint && (
              <RemoveCheckpointWarning tourPlans={tourPlansWithCheckpoint} />
            )}
          </ModalBody>
          <ModalFooter py='4'>
            <Button mr='3' onClick={handleOnClose} variant='ghost'>
              Cancel
            </Button>
            <Button
              isDisabled={!shouldEnableDeleteCheckpoint || canRemovedCheckpoint}
              isLoading={isLoading}
              leftIcon={<IoMdRemoveCircleOutline size={24} />}
              loadingText='Removing'
              onClick={handleRemoveCheckPoint}
              variant='danger'
            >
              Remove
            </Button>
          </ModalFooter>
        </>
      </ModalContent>
    </Modal>
  )
}
